import uuidv4 from 'uuid/v4';
import basic from './common/basic.js';
import { unwrapMention, setMentionContent } from '../mention-helper.js';
import updater from '../updater.js';
/* State example:
state: {
  "byId": {
    "con_8": {
      "id": "con_8",
      "label": "",
      "sourceid": "elem_9",
      "targetid": "elem_10"
    },
    "con_9": {
      "id": "con_9",
      "label": "There's rum, you know",
      "sourceid": "elem_10",
      "targetid": "elem_15"
    },
    "con_10": {
      "id": "con_10",
      "label": "These ropes...",
      "sourceid": "elem_10",
      "targetid": "elem_12"
    }
  }
}
*/
const NEW_CONNECTION = {
  label: null,
  theme: 'default',
};

/**
 * These are the types of modules that may be connected by connections
 */
const connectableTypes = {
  source: ['elements', 'conditions'],
  target: ['elements', 'branches', 'jumpers'],
};

const state = { byId: {} };

const getters = {
  ...basic.getters({}),

  /**
   * Return an array with the ids of the connections of a connectable (element, jumper, branch, condition).
   * Searches all connections and returns the connection ids that have this connectable as source or target.
   * @param   {String} id
   * @returns {Array}             The connection ids
   */
  getConnections(state) {
    return id => {
      // Filter only the connections that have this connectable as source or target
      return Object.values(state.byId)
        .filter(con => id === con.sourceid || id === con.targetid)
        .map(c => c.id);
    };
  },

  /**
   * Return a string with the type of a connectable (or null if it is not a connectable)
   * @param   {String}    id         The id of the connectable to search
   * @param   {String}    direction  One of 'source', 'target' to search
   * @returns {String|null}          The type of the connectable
   */
  getConnectableType(state, getters, rootState, rootGetters) {
    return (id, direction) => {
      const types = connectableTypes[direction];
      let result = null;
      types.forEach(type => {
        if (rootGetters[`project/data/${type}/getNode`](id)) {
          result = type;
        }
      });
      return result;
    };
  },
};

const actions = {
  ...basic.actions({
    type: 'connections',
    container: { type: 'boards', prop: 'connections' },
  }),

  /**
   * Create a connection
   * @param {String}      boardId     The board that will host the connection
   * @param {Object}      props       The connection properties that override default properties
   * @param {Boolean}     apply       Whether to apply the patch after finishing
   * @param {String|null} movedId     Whether this creation comes from a move operation
   */
  customCreate(context, { boardId, props, apply, movedId = null }) {
    console.log('Creating connection with props:', props);
    // Apply also custom default styles
    const defaultStyle =
      context.rootGetters['project/config/connections/getDefaultStyle'];

    const id = uuidv4();
    const connection = { id, ...NEW_CONNECTION, ...defaultStyle, ...props };

    console.log('Final connection node is:', connection);
    context.dispatch('create', {
      node: connection,
      tId: boardId,
      tIndex: '-',
      apply: false,
      movedId,
    });

    // Create the ouput reference on the element/condition
    const { sourceid } = connection;
    const type =
      context.getters.getConnectableType(sourceid, 'source') ||
      connection.sourceType;
    console.log(
      'customCreate(): Creating a connection:',
      connection,
      'for source type:',
      type
    );
    if (type === 'elements') {
      console.log('Connection source type is elements');
      context.dispatch(
        'project/data/elements/addOutput',
        {
          id: sourceid,
          outputId: connection.id,
          index: '-',
          apply: false,
        },
        { root: true }
      );
    } else if (type === 'conditions') {
      console.log(
        'Connection source type is conditions, updating condition output to',
        connection.id
      );
      context.dispatch(
        'project/data/conditions/customUpdate',
        {
          id: sourceid,
          props: { output: connection.id },
          apply: false,
        },
        { root: true }
      );
    }

    updater.complete({ apply });
    return Promise.resolve(id);
  },

  /**
   * Update some of the connection's properties (connection id should be filled)
   * @param {String}  id      The connection id
   * @param {Object}  props   The properties that are new or changed
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  customUpdate(context, { id, props, apply }) {
    context.dispatch('update', { id, props, apply: false });
    updater.complete({ apply });
  },

  /**
   * Delete a connection
   * @param {String}  id      The connection id
   * @param {Boolean} deleteReferences Whether to delete the references of the connection
   * @param {Booelan} apply   Whether to apply the patch after finishing
   */
  customDelete(context, { id, deleteReferences = true, apply }) {
    console.log('Removing a connection:', id, deleteReferences, apply);
    // Delete the connection
    context.dispatch('delete', { id, apply: false });
    if (deleteReferences) {
      // Delete any source references to it
      context.dispatch('deleteReferences', { ids: [id], apply: false });
    }
    // Deselect it
    context.dispatch(
      'project/config/connections/deselect',
      { id, apply: false },
      { root: true }
    );
    updater.complete({ apply });
  },

  /**
   * Remove mentions in all connections.
   * @param {array}   ids       The ids of the mentioned objects
   * @param {String}  type      The type of the mentioned objects
   * @param {Boolean} apply     Whether to apply the patch after finishing
   */
  deleteMentions(context, { ids, type, apply }) {
    const patch = [];
    Object.values(context.state.byId).forEach(connection => {
      const text = unwrapMention(connection.label, ids, type);
      if (text !== connection.label) {
        patch.push({
          op: 'replace',
          path: `/data/connections/byId/${connection.id}/label`,
          value: text,
        });
      }
    });
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Update the name in mentions in all connections.
   * @param {String}  id       The id of the mentioned object
   * @param {String}  type     The type of the mentioned object
   * @param {String}  name     The new name of the mentioned object
   * @param {String}  label The original label of the mention if different from name
   * @param {Boolean} apply    Whether to apply the patch after finishing
   */
  updateMentions(context, { id, type, name, label, apply }) {
    const patch = [];
    Object.values(context.state.byId).forEach(connection => {
      const text = setMentionContent(connection.label, id, type, name, label);
      if (text !== connection.label) {
        patch.push({
          op: 'replace',
          path: `/data/connections/byId/${connection.id}/label`,
          value: text,
        });
      }
    });
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove all connections where the given connectable (element, jumper, branch, condition) appears as source or target.
   * @param {String}  id          The id of the connectable
   * @param {Boolean} apply       Whether to apply the patch after finishing
   */
  deleteConnections(context, { id, apply }) {
    const connections = context.getters.getConnections(id).reverse();
    connections.forEach(cid => {
      context.dispatch(
        'project/config/connections/deselect',
        {
          id: cid,
          apply: false,
        },
        { root: true }
      );
      context.dispatch('delete', { id: cid, apply: false });
    });

    updater.complete({ apply });
  },

  /**
   * Remove any references to connections from any connectable (element, jumper, branch, condition).
   * @param {Array}  ids         The ids of the connections
   * @param {Boolean} apply      Whether to apply the patch after finishing
   */
  deleteReferences(context, { ids, apply }) {
    // Split the references to element outputs and condition outputs
    const elementOutputs = ids.filter(
      id => context.getters.getNode(id).sourceType === 'elements'
    );
    const conditionOutputs = ids.filter(
      id => context.getters.getNode(id).sourceType === 'conditions'
    );
    // Sort the element outputs by reverse index order
    elementOutputs.sort((a, b) => {
      const getIndex = context.rootGetters['project/data/elements/getIndex'];
      const aIndex = getIndex(a, 'elements', 'outputs');
      const bIndex = getIndex(b, 'elements', 'outputs');
      return bIndex - aIndex; // Descending order
    });
    // Delete all element output references
    elementOutputs.forEach(id => {
      const { sourceid } = context.getters.getNode(id);
      context.dispatch(
        'project/data/elements/deleteOutput',
        { id: sourceid, outputId: id, apply: false },
        { root: true }
      );
    });
    // Delete all condition output references (make them null)
    conditionOutputs.forEach(id => {
      const { sourceid } = context.getters.getNode(id);
      context.dispatch(
        'project/data/conditions/customUpdate',
        { id: sourceid, props: { output: null }, apply: false },
        { root: true }
      );
    });
    updater.complete({ apply });
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations: {},
  actions,
};

export default module;
